import { useLocalSearchParams } from "expo-router";
import { useEffect, useRef } from "react";
import { Animated, Easing, Platform, StyleSheet, View } from "react-native";

import type { Performance } from "@recital-master/shared";
import {
  countWatchedDancesByPerformance,
  formatDuration,
  formatDurationLong
} from "@recital-master/shared";

import { AppHeader } from "@/components/AppHeader";
import { Artwork } from "@/components/Artwork";
import { MediaCard, type MediaCardLayout } from "@/components/MediaCard";
import { PinkChip } from "@/components/PinkChip";
import { RecitalHero } from "@/components/RecitalHero";
import { useAccount } from "@/data/AccountProvider";
import { selectPerformanceFeaturedDances } from "@/data/catalog";
import {
  useCatalog,
  useDances,
  useMediaUrl,
  usePerformances,
  usePerformanceFeaturedArtwork,
  useRecitalArtwork
} from "@/data/CatalogProvider";
import { pageBackdropPresentation } from "@/design/backdropTone";
import { Button } from "@/design/Button";
import { PlayFromStartIcon, PlayIcon } from "@/design/icons";
import { ContentSection, Screen } from "@/design/primitives";
import { Muted, Text } from "@/design/Text";
import { palette, radius, space } from "@/design/tokens";
import { artGradient } from "@/lib/color";
import { useAppRouter } from "@/lib/appRouter";
import { displayTitle, DOT_SEPARATOR, eventDateLabel, joinWithDot } from "@/lib/format";
import { dancersPath, performancePath, useRecitalBySlug } from "@/lib/routes";
import {
  myDancerPlayLabel,
  savedDancersForRecital
} from "@/playback/myDancer";
import { recitalMyDancerWatchPath } from "@/playback/watchRoute";
import { useReadinessHold } from "@/providers/ReadinessGate";

function DanceFan({
  centered,
  performance,
  lit,
  layout
}: {
  centered: boolean;
  performance: Performance;
  lit: boolean;
  layout: MediaCardLayout;
}) {
  const dances = useDances(performance.performanceId);
  const mediaUrl = useMediaUrl();
  const spread = useRef(new Animated.Value(lit ? 1 : 0)).current;

  useEffect(() => {
    Animated.timing(spread, {
      toValue: lit ? 1 : 0,
      duration: 220,
      easing: Easing.bezier(0.33, 1, 0.68, 1),
      useNativeDriver: Platform.OS !== "web"
    }).start();
  }, [lit, spread]);

  const picked = dances.length
    ? selectPerformanceFeaturedDances(performance, dances)
    : [null, null, null];
  const positions = picked.length === 1 ? ["front"] : picked.length === 2 ? ["front", "back"] : ["front", "middle", "back"];
  const tiles = picked.map((dance, index) => ({ dance, position: positions[index], index })).reverse();

  const tileWidth = layout === "compact" ? 78 : layout === "split" ? 104 : 128;
  const tileHeight = Math.round(tileWidth * 9 / 16);
  const groupLeft = 0;
  const shelfHeight = Math.ceil(tileHeight + 12 + tileWidth * 0.14 + 8);
  return (
    <View
      style={[
        layout === "split" || layout === "compact"
          ? [
              styles.fan,
              {
                height: shelfHeight,
                transform: [
                  { translateX: centered ? 0 : space.sm },
                  { translateY: layout === "compact" ? space.sm : space.md }
                ],
                width: Math.ceil(tileWidth * 1.5 + tileWidth * 0.14)
              }
            ]
          : [
              styles.fan,
              {
                transform: [
                  { translateY: space.sm }
                ]
              }
            ],
        { pointerEvents: "none" }
      ]}
    >
      {tiles.map(({ dance, position, index }) => {
        const isBack = position === "back";
        const isMiddle = position === "middle";
        const transform = (() => {
              const backDrift = layout === "wide" ? -8 : -6;
              const frontDrift = layout === "wide" ? 10 : 8;
              return [
                { translateX: isBack ? spread.interpolate({ inputRange: [0, 1], outputRange: [0, backDrift] }) : isMiddle ? tileWidth * 0.25 : spread.interpolate({ inputRange: [0, 1], outputRange: [tileWidth * 0.5, tileWidth * 0.5 + frontDrift] }) },
                { translateY: isBack ? spread.interpolate({ inputRange: [0, 1], outputRange: [8, 4] }) : isMiddle ? 0 : spread.interpolate({ inputRange: [0, 1], outputRange: [12, 14] }) },
                { rotate: isBack ? spread.interpolate({ inputRange: [0, 1], outputRange: ["-8deg", "-11deg"] }) : isMiddle ? "-2deg" : spread.interpolate({ inputRange: [0, 1], outputRange: ["5deg", "7.5deg"] }) },
                { scale: isBack ? 0.9 : isMiddle ? 0.95 : 1 }
              ];
          })();
        return (
          <Animated.View
            key={dance?.danceId ?? `placeholder-${index}`}
            style={[styles.fanTile, { height: tileHeight, width: tileWidth }, { opacity: isBack ? spread.interpolate({ inputRange: [0, 1], outputRange: [0.5, 0.65] }) : isMiddle ? spread.interpolate({ inputRange: [0, 1], outputRange: [0.75, 0.88] }) : 1, transform }]}
          >
            <Artwork
              uri={dance ? mediaUrl(dance.thumbPath) : null}
              placeholderUri={dance?.blurThumb}
              fallbackSeed={dance?.danceId ?? `${performance.performanceId}-fan-${index}`}
              style={styles.fanArtwork}
            />
          </Animated.View>
        );
      })}
      {performance.danceCount > 3 ? (
        // Stamped on the front photo's lower-right corner in every layout —
        // anchored to the tile itself, never the container's bleed padding.
        <View
          style={[
            styles.countChip,
            { left: groupLeft + tileWidth * 1.5 - 34, top: 12 + tileHeight - 11 }
          ]}
        >
          <Text variant="smallStrong" color={palette.textSoft}>+{performance.danceCount - 3}</Text>
        </View>
      ) : null}
    </View>
  );
}

function PerformanceCard({ performance, toPath, index }: { performance: Performance; toPath: string; index: number }) {
  const router = useAppRouter();
  const { catalog, preparePerformance } = useCatalog();
  const artwork = usePerformanceFeaturedArtwork(performance.performanceId);
  const { resume } = useAccount();

  const storedResumeState = resume[performance.performanceId];
  const resumeState = storedResumeState?.completed ? undefined : storedResumeState;
  const resumeDance = resumeState
    ? catalog?.dances[performance.performanceId]?.find(
        (dance) => dance.danceId === resumeState.danceId
      ) ?? null
    : null;

  const metaParts = [
    `${performance.danceCount} dances`,
    formatDurationLong(performance.totalDurationSeconds)
  ];

  const navigatePrepared = (path: string) => {
    void preparePerformance(performance.performanceId)
      .catch(() => {})
      .then(() => router.push(path as never));
  };

  return (
    <MediaCard
      title={performance.title}
      kicker={joinWithDot([`Show ${index + 1}`, performance.subtitle])}
      metaLines={[eventDateLabel(performance.eventDate), joinWithDot(metaParts)]}
      metaRow={{ leading: eventDateLabel(performance.eventDate), trailing: joinWithDot(metaParts) }}
      artworkUri={artwork}
      saturateBackdrop
      posterSlot={({ centered, lit, layout }) => (
        <DanceFan
          centered={centered}
          performance={performance}
          lit={lit}
          layout={layout}
        />
      )}
      accessibilityLabel={`Open ${performance.title}`}
      onPress={() => navigatePrepared(toPath)}
      action={
        resumeState && resumeDance
          ? {
              overline: joinWithDot(["Resume", `${formatDuration(Math.max(0, resumeDance.durationSeconds - resumeState.positionSeconds))} left`]),
              label: displayTitle(resumeDance.title),
              onPress: () =>
                navigatePrepared(
                  `/watch/${performance.performanceId}?mode=${resumeState.mode}&dance=${resumeState.danceId}&at=${Math.floor(resumeState.positionSeconds)}`
                )
            }
          : {
              overline: joinWithDot(["Watch", formatDurationLong(performance.totalDurationSeconds)]),
              label: "Play from start",
              onPress: () => navigatePrepared(`/watch/${performance.performanceId}?mode=full-performance`)
            }
      }
    />
  );
}

export default function RecitalScreen() {
  const { studioSlug, recitalSlug } = useLocalSearchParams<{
    studioSlug: string;
    recitalSlug: string;
  }>();
  const router = useAppRouter();
  const recital = useRecitalBySlug(studioSlug, recitalSlug);
  const performances = usePerformances(recital?.recitalId);
  const artwork = useRecitalArtwork(recital?.recitalId);
  const { recitalResume, resume, savedDancers } = useAccount();
  const { catalog, performanceDetailErrors, preparePerformance, status } = useCatalog();
  const studio = catalog?.studios.find(
    (entry) => entry.studioId === recital?.studioId
  );
  const performanceDataSettled = performances.every(
    (performance) => catalog?.dances[performance.performanceId] || performanceDetailErrors[performance.performanceId]
  );
  useReadinessHold(
    `recital-details-${studioSlug ?? "unknown"}-${recitalSlug ?? "unknown"}`,
    status === "ready" && (!recital || performanceDataSettled)
  );

  useEffect(() => {
    for (const performance of performances) void preparePerformance(performance.performanceId).catch(() => {});
  }, [performances, preparePerformance]);

  if (!recital) {
    if (status !== "ready") return null;
    return (
      <Screen header={<AppHeader back />}>
        <View style={styles.missing}>
          <Text variant="title">This recital isn’t in your library.</Text>
          <Muted>Everything you can watch lives in your recitals.</Muted>
          <Button
            label="Go to my recitals"
            variant="secondary"
            onPress={() => router.replace("/recitals")}
          />
        </View>
      </Screen>
    );
  }

  const danceCount = performances.reduce((sum, performance) => sum + performance.danceCount, 0);
  const totalDuration = performances.reduce((sum, performance) => sum + performance.totalDurationSeconds, 0);
  const allDances = performances.flatMap((performance) => catalog?.dances[performance.performanceId] ?? []);
  const recitalSavedDancers = savedDancersForRecital(savedDancers, recital.recitalId);
  const recitalSavedDancerIds = new Set(
    recitalSavedDancers.map((dancer) => dancer.dancerId)
  );
  const storedRecitalResume = recitalResume[recital.recitalId];
  const activeRecitalResume = storedRecitalResume?.completed
    ? undefined
    : storedRecitalResume;
  const recitalResumeQueue =
    activeRecitalResume?.mode === "my-dancer"
      ? allDances.filter((dance) =>
          dance.dancers.some((dancer) => recitalSavedDancerIds.has(dancer.dancerId))
        )
      : recital.intro
        ? [recital.intro, ...allDances]
        : allDances;
  const recitalResumeDance = activeRecitalResume
    ? recitalResumeQueue.find((dance) => dance.danceId === activeRecitalResume.danceId)
    : undefined;
  const recitalResumeIndex = recitalResumeDance
    ? recitalResumeQueue.findIndex((dance) => dance.danceId === recitalResumeDance.danceId)
    : -1;
  const recitalRemainingSeconds =
    activeRecitalResume && recitalResumeIndex >= 0
      ? Math.max(
          0,
          recitalResumeQueue
            .slice(recitalResumeIndex)
            .reduce((sum, dance) => sum + dance.durationSeconds, 0) -
            activeRecitalResume.positionSeconds
        )
      : 0;
  const recitalWatchedIds = new Set(storedRecitalResume?.watchedDanceIds ?? []);
  const watchedDanceCount = countWatchedDancesByPerformance(
    performances.map((performance) => ({
      danceIds: (catalog?.dances[performance.performanceId] ?? []).map((dance) => dance.danceId),
      watchedDanceIds: [
        ...(resume[performance.performanceId]?.watchedDanceIds ?? []),
        ...(catalog?.dances[performance.performanceId] ?? [])
          .filter((dance) => recitalWatchedIds.has(dance.danceId))
          .map((dance) => dance.danceId)
      ],
      danceCount: performance.danceCount
    }))
  );
  const completionProgress =
    watchedDanceCount > 0 && danceCount > 0
      ? {
          fraction: watchedDanceCount / danceCount,
          label:
            watchedDanceCount === danceCount
              ? `All ${danceCount} watched`
              : `${watchedDanceCount} of ${danceCount} watched`
        }
      : undefined;
  const tint = artGradient(recital.artColors);
  const navigateToFullRecital = (path: string) => {
    void Promise.all(performances.map((performance) => preparePerformance(performance.performanceId).catch(() => {})))
      .then(() => router.push(path as never));
  };
  const fullRecitalPath = `/watch/${recital.recitalId}?mode=full-recital`;
  const recitalDancerPath = recitalMyDancerWatchPath(recital.recitalId);
  const actionsBlock = (
    <>
      {activeRecitalResume && recitalResumeDance ? (
        <Button
          label={joinWithDot([
            "Resume",
            `${formatDurationLong(recitalRemainingSeconds)} left`
          ])}
          tint={tint}
          icon={<PlayIcon size={16} color={palette.inkOnAccent} />}
          onPress={() =>
            navigateToFullRecital(
              activeRecitalResume.mode === "my-dancer"
                ? recitalMyDancerWatchPath(recital.recitalId, {
                    danceId: activeRecitalResume.danceId,
                    positionSeconds: activeRecitalResume.positionSeconds
                  })
                : `${fullRecitalPath}&dance=${activeRecitalResume.danceId}&at=${Math.floor(activeRecitalResume.positionSeconds)}`
            )
          }
        />
      ) : null}
      <Button
        label="Play full recital"
        variant={activeRecitalResume ? "secondary" : "primary"}
        tint={tint}
        icon={
          <PlayFromStartIcon
            size={16}
            color={activeRecitalResume ? palette.text : palette.inkOnAccent}
          />
        }
        onPress={() => navigateToFullRecital(fullRecitalPath)}
      />
      {recitalSavedDancers.length > 0 ? (
        <PinkChip
          label={myDancerPlayLabel(recitalSavedDancers)}
          onPress={() => navigateToFullRecital(recitalDancerPath)}
        />
      ) : (
        <PinkChip
          label="Find my dancer"
          onPress={() => router.push(dancersPath(recital) as never)}
        />
      )}
    </>
  );
  const pageBackdrop = pageBackdropPresentation(artwork, "program-cover");

  return (
    // This page's backdrop is the program cover, whose title sits mid-frame.
    // Resolve its URI and focal role together so other cover-backed surfaces
    // can share the exact same presentation contract.
    <Screen
      backdropUri={pageBackdrop.uri}
      backdropFocalY={pageBackdrop.focalY}
      header={<AppHeader back />}
    >
      <RecitalHero
        artworkUri={artwork}
        fallbackSeed={recital.recitalId}
        artworkLabel={String(recital.year)}
        eyebrow={joinWithDot([studio?.name, `${recital.year} Recital`])}
        title={recital.title}
        meta={`${performances.length} ${performances.length === 1 ? "show" : "shows"}${DOT_SEPARATOR}${danceCount} dances${DOT_SEPARATOR}${formatDurationLong(totalDuration)}`}
        progress={completionProgress}
      >
        {actionsBlock}
      </RecitalHero>

      <ContentSection label="Recital shows">
        <View style={styles.list}>
          {performances.map((performance, index) => (
            <PerformanceCard
              key={performance.performanceId}
              performance={performance}
              index={index}
              toPath={performancePath(recital, performance.performanceId)}
            />
          ))}
        </View>
      </ContentSection>
    </Screen>
  );
}

const styles = StyleSheet.create({
  fan: {
    alignSelf: "center",
    height: 104,
    position: "relative",
    width: 208
  },
  fanTile: {
    borderColor: palette.hairline,
    borderRadius: radius.md,
    borderWidth: 1,
    height: 72,
    left: 0,
    overflow: "hidden",
    position: "absolute",
    ...Platform.select({
      native: {
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 6 },
        shadowOpacity: 0.35,
        shadowRadius: 12
      },
      web: {
        boxShadow: "0 6px 12px rgba(0, 0, 0, 0.35)",
        willChange: "transform"
      }
    }),
    top: 0,
    width: 128
  },
  fanArtwork: {
    height: "100%",
    width: "100%"
  },
  countChip: {
    backgroundColor: "rgba(13,14,20,0.72)",
    borderColor: palette.hairline,
    borderRadius: 999,
    borderWidth: 1,
    paddingHorizontal: 8,
    paddingVertical: 3,
    position: "absolute",
    zIndex: 4
  },
  list: {
    gap: space.lg
  },
  missing: {
    alignItems: "flex-start",
    gap: space.lg,
    marginTop: space.xl
  }
});
